import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of, throwError, forkJoin } from 'rxjs';
import { catchError, finalize, switchMap, map } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import { sinInterceptorGlobal } from 'src/app/shared/utils/error-http-real';

/**
 * Modo de cálculo de la tasa VES↔USDT.
 * - AUTOMATICO_PCT: promedio Binance × (1 ± comisión %)
 * - MANUAL_FIJO: precio fijo configurado por el operador
 */
export type RateMode = 'AUTOMATICO_PCT' | 'MANUAL_FIJO';

/** Payload de guardado de config VES↔USDT (comisión % o precio fijo). */
export interface SaveRateConfigPayload {
  modo: RateMode;
  comisionPct?: number;
  tasaFija?: number;
}

export interface CalcularTasasResponse {
  success: boolean;
  message: string;
  data: any;
}

export interface UltimasTasasResponse {
  success: boolean;
  data: any[];
}

export interface TasasPorRangoResponse {
  success: boolean;
  data: any;
}

export interface TasaPorMontoResponse {
  id: number;
  montoCLP: number;
  montoVES: number;
  rango: string;
  rangoNumerico: string;
  fuente: string;
  perfil: string;
  tasaClient: number;
  tasaAdmin: number;
  tasaAplicada: number;
  descripcion: string;
  fechaTasa: string;
}

export interface ValoresFrontendResponse {
  success: boolean;
  message: string;
  data?: {
    operadorActivo: string;
    parametrosManual: any;
    parametrosBinance: any;
    parametrosCbpay: any;
    tasasActuales: any;
  } | null;
}

// ───────────────────────── Franjas dinámicas (CLP↔VES) ─────────────────────────

export type ParFranja = 'CLP-VES' | 'VES-CLP';
export type ModoFranja = 'DESCUENTOS' | 'FIJO' | 'PCT';

/** Una franja tal como la guarda el backend. `desde`/`hasta` en CLP. */
export interface Franja {
  id?: number;
  par: ParFranja;
  clave: string;
  orden: number;
  desde: number;
  hasta: number;
  modo: ModoFranja;
  ajuste: number;
  activo: boolean;
}

/** Lo que manda el editor. Sin `clave` = franja nueva. */
export interface EntradaFranja {
  clave?: string;
  desde: number;
  hasta: number;
  modo: ModoFranja;
  ajuste: number;
  activo: boolean;
}

/** Tasa calculada de una franja con los precios de este momento. */
export interface TasaFranja {
  clave: string;
  letra: string;
  orden: number;
  desde: number;
  hasta: number;
  modo: ModoFranja;
  ajuste: number;
  client: number;
  admin: number;
  margenPct: number;
  /**
   * Aditivo (motor de márgenes, 03/11/2026): % que le toca a la franja sobre
   * el spot cruzado. Null en LEGADO; ausente con un back viejo.
   */
  margenAplicado?: number | null;
  /**
   * Aditivo: la tasa en la unidad de la ruta. CLP-VES: CLP por 1 VES;
   * VES-CLP: VES por 1 CLP. Null en LEGADO; ausente con un back viejo.
   */
  tasaRuta?: number | null;
}

// ───────────────────────── Motor de márgenes CLP↔VES (03/11/2026) ─────────────────────────

/**
 * Motor de las tasas CLP↔VES.
 * - LEGADO: comisión y descuentos de siempre.
 * - MARGENES: spot cruzado (USD/CLP y el VES/USDT del operador activo) más un
 *   margen por franja que se reparte solo entre el mínimo y el máximo.
 */
export type MotorTasasVes = 'LEGADO' | 'MARGENES';

/**
 * Márgenes de una ruta, en %. `minimo` = la MEJOR tasa (el límite, montos
 * grandes); `maximo` = la tasa base (montos chicos).
 */
export interface RangoMargenVes {
  minimo: number;
  maximo: number;
}

/** Los márgenes de las dos rutas. `null` = faltan en la BD o no son válidos. */
export interface MargenesVes {
  clpVes: RangoMargenVes | null;
  vesClp: RangoMargenVes | null;
}

/** Una franja activa con el margen que le toca y su tasa. */
export interface TramoMargenVes {
  clave: string;
  letra: string;
  desde: number;
  hasta: number;
  margenAplicado: number | null;
  /** CLP-VES: CLP por 1 VES; VES-CLP: VES por 1 CLP. */
  tasaRuta: number | null;
  /** Lo que se guarda: VES por 1 CLP en las dos rutas. */
  tasaClient: number | null;
}

/** Spot cruzado: `vesClp` = VES por 1 CLP; `clpVes` = CLP por 1 VES. */
export interface SpotCruzadoVes {
  vesClp: number;
  clpVes: number;
}

/**
 * GET rates/margenes-ves (operadores). `tramos` es el reparto con los
 * márgenes GUARDADOS sobre las franjas activas (también con el motor LEGADO);
 * una ruta sin márgenes puede venir vacía.
 */
export interface MargenesVesResponse {
  motor: MotorTasasVes;
  margenes: MargenesVes;
  /** Las dos rutas tienen sus márgenes. */
  completos: boolean;
  /** Tope de cada margen, en % (hoy 20). */
  limiteMaximoPct: number;
  operador: string | null;
  usdClp: number | null;
  rateVesUsdt: number | null;
  spotCruzado: SpotCruzadoVes | null;
  tramos: {
    clpVes: TramoMargenVes[];
    vesClp: TramoMargenVes[];
  };
  /**
   * Aditivo: versión opaca de lo guardado (motor y márgenes). Viaja en el PUT:
   * si otro operador guardó antes, 409 y no se guarda nada. Null (o ausente)
   * con un back que todavía no la manda: el PUT sale sin ella, como antes.
   */
  version?: string | null;
}

/** PUT rates/margenes-ves. Sin `motor`, el back conserva el actual. */
export interface GuardarMargenesVesBody {
  clpVes: RangoMargenVes;
  vesClp: RangoMargenVes;
  motor?: MotorTasasVes;
  /** Opcional, hasta 255 caracteres. */
  motivo?: string;
  /** La `version` del GET, si vino: si no coincide con lo guardado, 409 `{ message }`. */
  version?: string;
}

export interface GuardarMargenesVesResponse {
  motor: MotorTasasVes;
  margenes: MargenesVes;
  aplicado: { clpVes: boolean; vesClp: boolean };
  mensaje: string;
  /** Aditivo: la versión nueva de lo guardado. */
  version?: string | null;
  /**
   * Aditivo: al volver de MARGENES a LEGADO con franjas que rompen las reglas
   * de LEGADO, cuáles y por qué (también lo dice `mensaje`).
   */
  advertencias?: string[];
}

/**
 * Aditivo (03/11/2026) en `POST rates/actualizar-parametros`,
 * `POST rates/cambiar-operador` y `PUT rates/bancos-config`: si se grabaron
 * las tasas de cada ruta con lo guardado. Con alguna en false, `message` dice
 * cuál y por qué, y `success` sigue en true (los parámetros sí quedaron).
 * Un back que no lo manda se lee como antes.
 */
export interface TasasGrabadas {
  clpVes: boolean;
  vesClp: boolean;
}

/**
 * Las franjas de la tabla rigen siempre. `fuente: 'CONSTANTE'` = la tabla no
 * se pudo usar y el backend cayó a los tramos de siempre (respaldo interno).
 */
export interface EstadoFranjasResponse {
  pares: Array<{
    par: ParFranja;
    fuente: 'TABLA' | 'CONSTANTE';
    franjas: Franja[];
    limites: { min: number; max: number } | null;
  }>;
}

export interface HistorialFranja {
  id: number;
  tramoId: number;
  par: ParFranja;
  clave: string;
  accion: 'SEMILLA' | 'CREAR' | 'EDITAR' | 'APAGAR' | 'ENCENDER';
  antes: any;
  despues: any;
  editadoPor: string;
  motivo: string;
  createdAt: string;
}

export interface EditorFranjasResponse {
  par: ParFranja;
  franjas: Franja[];
  tasas: TasaFranja[];
  mid: number | null;
  /** Tasa base con comisión (la de la franja con 0 descuentos). */
  base?: number | null;
  insumos: {
    usdClp: number;
    rateVesUsdt: number;
    comision: number;
    descuentoFiat: number;
    comisionVesClp?: number;
  } | null;
  /** Los últimos 5 cambios. El resto se pide paginado. */
  historial: HistorialFranja[];
  /** Cantidad total de cambios del par (para la paginación). */
  historialTotal?: number;
  /**
   * Versión de lo guardado (aditivo del back). Se devuelve en el PUT: si otro
   * operador guardó antes, el back responde 409. Sin ella se guarda como antes.
   */
  version?: string;
  /** Aditivo (03/11/2026): motor de las tasas. Ausente (back viejo) = LEGADO. En MARGENES `base` = mid. */
  motor?: MotorTasasVes;
  /** Márgenes de este par; null si faltan en la BD. */
  margenes?: RangoMargenVes | null;
  /** Spot cruzado del par: CLP-VES → CLP por 1 VES; VES-CLP → VES por 1 CLP. */
  spotCruzado?: number | null;
}

export interface PrevisualizarFranjasResponse {
  par: ParFranja;
  ok: boolean;
  errores: string[];
  advertencias: string[];
  franjas: Franja[];
  tasas: TasaFranja[];
  tasasActuales: TasaFranja[];
  mid: number | null;
  base?: number | null;
  /** Aditivos (03/11/2026): los mismos del GET del editor. */
  motor?: MotorTasasVes;
  margenes?: RangoMargenVes | null;
  spotCruzado?: number | null;
}

export interface HistorialFranjasPagina {
  items: HistorialFranja[];
  total: number;
  pagina: number;
  tamano: number;
}

export interface GuardarFranjasResponse {
  par: ParFranja;
  cambios: number;
  /**
   * Las tasas nuevas ya quedaron grabadas. En false, `mensaje` dice por qué:
   * el lock estaba ocupado (se grabarán en unos segundos) o el motor no grabó
   * (con el motivo, ver `motivoNoAplicado`).
   */
  aplicado: boolean;
  /**
   * Aditivo: con `aplicado: false`, por qué el motor no grabó las tasas
   * («Franjas guardadas, pero las tasas de <ruta> no se grabaron: <motivo>.
   * Siguen las últimas.» en `mensaje`). Null = el lock estaba ocupado y se
   * grabarán en unos segundos. Ausente con un back viejo.
   */
  motivoNoAplicado?: string | null;
  advertencias: string[];
  mensaje: string;
}

// ───────────────────────── Tramos VES→USDT ─────────────────────────

export type ModoTramoUsdt = 'AUTO' | 'PCT';

/** Tasa de un tramo activo con la base vigente. `desde`/`hasta` en USDT. */
export interface TasaTramoUsdt {
  clave: string;
  orden: number;
  desde: number;
  hasta: number;
  modo: ModoTramoUsdt;
  /** VES por USDT, dos decimales. */
  tasa: number;
  /** Margen sobre el costo de mercado, en %. */
  margenPct: number;
  /** Cuánto mejora la base para el cliente, en %. */
  mejoraPct: number;
  /** El margen pedido quedó recortado por el candado. */
  recortado: boolean;
}

/** Un tramo activo tal como lo ve el cliente: sin costo ni margen. */
export interface TramoClienteUsdt {
  clave: string;
  orden: number;
  desde: number;
  hasta: number;
  /** VES por USDT, dos decimales. */
  tasa: number;
  /** Cuánto mejora la base para el cliente, en %. */
  mejoraPct: number;
}

/**
 * GET rates/tramos-usdt/ves-usdt (cualquier usuario logueado). Solo base y
 * tramos activos ordenados: costo, márgenes y modo son de operadores y van
 * en `/editor`. `tramos` vacío = todos pagan la base.
 */
export interface EstadoTramosUsdtResponse {
  base: number | null;
  fechaBase: string | null;
  tramos: TramoClienteUsdt[];
  /** Tasa manual vigente de VES→USDT y hasta cuándo rige (aditivo del back). */
  manualVigente?: ManualVigenteTramos | null;
  /** Aditivo (23/10): precio del USDT con que se pasa el monto a USDT para elegir el tramo. Null/ausente = la base. */
  precioCorte?: number | null;
}

/** Tasa manual con vencimiento que pisa la base de VES→USDT. */
export interface ManualVigenteTramos {
  tasa: number;
  hasta: string;
}

/** Un tramo tal como lo guarda el back (activos y apagados). */
export interface TramoUsdtGuardado {
  clave: string;
  orden: number;
  desde: number;
  hasta: number;
  modo: ModoTramoUsdt;
  ajuste: number;
  activo: boolean;
}

/** Lo que manda el editor. Sin `clave` = tramo nuevo. */
export interface EntradaTramoUsdt {
  clave?: string;
  desde: number;
  hasta: number;
  modo: ModoTramoUsdt;
  ajuste: number;
  activo: boolean;
}

/** Sin comisión: se cambia en su modal de siempre (mueve la base de Vita). */
export interface CuerpoTramosUsdt {
  margenMinPct: number;
  tramos: EntradaTramoUsdt[];
}

/** Cambios de comisión y margen mínimo llegan con clave `_comision` / `_margen_min`. */
export interface HistorialTramoUsdt {
  id: number;
  clave: string;
  accion: string;
  antes: any;
  despues: any;
  editadoPor: string;
  motivo: string;
  createdAt: string;
}

export interface HistorialTramosUsdtPagina {
  items: HistorialTramoUsdt[];
  total: number;
  pagina: number;
  tamano: number;
}

export interface EditorTramosUsdtResponse {
  /** Solo lectura: se cambia en el modal de comisión VES→USDT. */
  comisionPct: number;
  margenMinPct: number;
  referencia: number | null;
  base: number | null;
  modoBase: RateMode | null;
  fechaBase: string | null;
  /** Tasa de venta USDT→VES: el piso no baja de ella + 0,5 %. Null si no se pudo leer. */
  tasaVentaUsdtVes: number | null;
  /** Tasa mínima de los tramos con lo guardado. */
  piso: number | null;
  tramos: TramoUsdtGuardado[];
  tasas: TasaTramoUsdt[];
  historial: HistorialTramosUsdtPagina;
  /** Versión de lo guardado (aditivo del back): viaja en el PUT; 409 si cambió. */
  version?: string;
  /** Tasa manual vigente y hasta cuándo rige (aditivo del back). */
  manualVigente?: ManualVigenteTramos | null;
}

export interface PrevisualizarTramosUsdtResponse {
  ok: boolean;
  errores: string[];
  advertencias: string[];
  base: number | null;
  referencia: number | null;
  espacioPct: number;
  piso: number | null;
  tasas: TasaTramoUsdt[];
}

export interface GuardarTramosUsdtResponse extends EditorTramosUsdtResponse {
  ok: true;
  cambios: number;
}

// ───────────────────────── Tramos USDT→VES (espejo) ─────────────────────────

/**
 * GET rates/tramos-usdt/usdt-ves/editor. Misma forma que el de VES→USDT pero
 * con TECHO en vez de piso: el cliente VENDE USDT y una tasa más alta es mejor
 * para él. `tasas[].margenPct` es el margen BAJO la referencia y `mejoraPct`
 * cuánto paga de más sobre la base.
 */
export interface EditorTramosUsdtVesResponse {
  /** Solo lectura: se cambia en el modal de comisión USDT→VES. */
  comisionPct: number;
  margenMinPct: number;
  referencia: number | null;
  base: number | null;
  modoBase: RateMode | null;
  fechaBase: string | null;
  /** Tasa más baja de los tramos VES→USDT: el techo no pasa de ella ÷ 1,005. */
  mejorTasaCompraVesUsdt: number | null;
  /** Tasa máxima de los tramos con lo guardado. */
  techo: number | null;
  tramos: TramoUsdtGuardado[];
  tasas: TasaTramoUsdt[];
  historial: HistorialTramosUsdtPagina;
  /** Versión de lo guardado (aditivo del back): viaja en el PUT; 409 si cambió. */
  version?: string;
  /** Tasa manual vigente y hasta cuándo rige (aditivo del back). */
  manualVigente?: ManualVigenteTramos | null;
}

export interface PrevisualizarTramosUsdtVesResponse {
  ok: boolean;
  errores: string[];
  advertencias: string[];
  base: number | null;
  referencia: number | null;
  espacioPct: number;
  techo: number | null;
  tasas: TasaTramoUsdt[];
}

export interface GuardarTramosUsdtVesResponse extends EditorTramosUsdtVesResponse {
  ok: true;
  cambios: number;
}

// ───────────────────────── Tramos CLP→USDT y USDT→CLP (22/10) ─────────────────────────

/**
 * GET rates/tramos-usdt/clp-usdt (cualquier usuario logueado). Lo que se
 * COBRA: el snapshot de la fila base vigente de `valores_cambio` (`baseId`).
 * `desde`/`hasta` en USDT; el tramo se elige con `montoClp ÷ base`. `tramos`
 * vacío = todos pagan la base.
 */
export interface EstadoTramosClpUsdtResponse {
  base: number | null;
  baseId: number | string | null;
  fechaBase: string | null;
  /**
   * Aditivo (22/10/2026): piso con que el servidor recorta la segmentación
   * (`seg' = max(seg, piso)`). Null o ausente (backend viejo) = sin recorte.
   */
  pisoSegmentacion?: number | null;
  tramos: TramoClienteUsdt[];
  /** Aditivo (23/10): el tramo se elige con `montoClp ÷ precioCorte` (spot). Null/ausente = `÷ base`. */
  precioCorte?: number | null;
}

/** GET rates/tramos-usdt/usdt-clp: el tramo se elige con los USDT vendidos. */
export interface EstadoTramosUsdtClpResponse {
  base: number | null;
  fechaBase: string | null;
  tramos: TramoClienteUsdt[];
  /** Aditivo (23/10): precio del USDT para elegir el tramo de un monto en CLP. Null/ausente = sin conversión. */
  precioCorte?: number | null;
}

/** Cuerpo de CLP↔USDT: el de VES más el tope % (0 a 5; sin él se conserva el actual). */
export interface CuerpoTramosClp extends CuerpoTramosUsdt {
  topePct?: number;
}

/** Por qué un par CLP cobra la base sin tramos (null = rigen). */
export type MotivoSinTramosClp =
  | 'SIN_BASE'
  | 'MANUAL'
  | 'FUENTE_CONGELADA'
  | 'SPOT_INVALIDO'
  | 'SPOT_FUERA_DE_BANDA'
  | null;

/** Tramo publicado en el snapshot de la compra (lo que se está cobrando). */
export interface TramoPublicadoClp {
  clave: string;
  orden: number;
  desde: number;
  hasta: number;
  modo: string;
  tasa: number;
}

/**
 * GET rates/tramos-usdt/clp-usdt/editor. La referencia es `PRECIO_SPOT`; el
 * descuento (`DESCUENTO_1`) es solo lectura. El piso sale de margen mínimo,
 * tope y la base de venta + 0,5 %.
 */
export interface EditorTramosClpUsdtResponse {
  version?: string;
  /** `DESCUENTO_1`, solo lectura (se cambia en su modal de siempre). */
  descuento: number | null;
  margenMinPct: number;
  topePct: number;
  /** `PRECIO_SPOT`. */
  referencia: number | null;
  base: number | null;
  baseId: number | string | null;
  fechaBase: string | null;
  /** Base de venta USDT→CLP: el piso no baja de ella + 0,5 %. */
  baseVenta: number | null;
  motivoSinTramos: MotivoSinTramosClp;
  piso: number | null;
  tramos: TramoUsdtGuardado[];
  tasas: TasaTramoUsdt[];
  /** Lo que se cobra ahora (la edición rige desde la próxima fila base). */
  publicados: TramoPublicadoClp[];
  historial: HistorialTramosUsdtPagina;
}

/** GET rates/tramos-usdt/usdt-clp/editor: el espejo, con TECHO. */
export interface EditorTramosUsdtClpResponse {
  version?: string;
  /** `DESCUENTO_1_SELL`, solo lectura. */
  descuento: number | null;
  margenMinPct: number;
  topePct: number;
  referencia: number | null;
  base: number | null;
  fechaBase: string | null;
  /** Compra final más baja: el techo no pasa de ella ÷ 1,005. */
  compraMinFinal: number | null;
  motivoSinTramos: MotivoSinTramosClp;
  techo: number | null;
  tramos: TramoUsdtGuardado[];
  tasas: TasaTramoUsdt[];
  historial: HistorialTramosUsdtPagina;
}

export interface PrevisualizarTramosClpUsdtResponse {
  ok: boolean;
  errores: string[];
  advertencias: string[];
  base: number | null;
  referencia: number | null;
  piso: number | null;
  tasas: TasaTramoUsdt[];
}

export interface PrevisualizarTramosUsdtClpResponse {
  ok: boolean;
  errores: string[];
  advertencias: string[];
  base: number | null;
  referencia: number | null;
  techo: number | null;
  tasas: TasaTramoUsdt[];
}

/** Respuesta de `rates/markup/estado`. El backend ya hizo la cuenta. */
export interface MarkupEstadoResponse {
  success: boolean;
  message: string;
  data?: {
    spot: number;
    aguacate: number;
    ratio: number;
    markupPct: number;
    umbral: number;
    reaparicionSeg: number;
    sobreUmbral: boolean;
    operador: string | null;
    actualizadoEn: string;
    /** ISO hasta cuándo este operador tiene el aviso en silencio. */
    silencioHasta: string | null;
  } | null;
}

export interface ValoresRegistradosResponse {
  success: boolean;
  message: string;
  data?: {
    binance: {
      bancos: {
        banesco: number;
        provincial: number;
        mercantil: number;
        venezuela: number;
      };
      promedio: number;
      ultimaActualizacion: Date;
    };
    cbpay: {
      valor: number;
    };
    manual: {
      valor: number;
    };
    usdClp: number;
    operadorActivo: string;
  } | null;
}

export interface CambiarOperadorRequest {
  operador: 'MANUAL' | 'BINANCE' | 'CBPAY';
}

// Interfaces para USDT-VES
export interface UsdtVesConfigResponse {
  success: boolean;
  data: {
    tasaVigente: {
      id: number;
      promedioBinance: number;
      comisionPct: number;
      tasaFinal: number;
      bancosActivos: string[];
      valoresBancos: {
        banesco: number;
        provincial: number;
        mercantil: number;
        venezuela: number;
      };
      responsible: string;
      createdAt: string;
      modo?: RateMode;
    } | null;
    datosEnVivo: {
      promedioBinanceActual: number;
      comisionConfigurada: number;
      tasaFinalCalculada: number;
      bancos: Record<string, { valor: number; activo: boolean }>;
    };
    hayCambios: boolean;
    simulacion: {
      ejemploUsdt: number;
      resultadoVes: number;
    };
  };
}

export interface UsdtVesPreviewResponse {
  success: boolean;
  data: {
    promedioBinance: number;
    comisionPct: number;
    tasaFinal: number;
    ejemplos: { usdt: number; ves: number }[];
    bancosActivos: string[];
  };
}

export interface UsdtVesSaveResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    promedioBinance: number;
    comisionPct: number;
    tasaFinal: number;
    bancosActivos: string[];
    valoresBancos: any;
    responsible: string;
    createdAt: string;
    tasaAnterior?: {
      id: number;
      tasaFinal: number;
    };
  };
}

export interface UsdtVesHistoryResponse {
  success: boolean;
  data: {
    items: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Interfaces para VES-USDT
export interface VesUsdtConfigResponse {
  success: boolean;
  data: {
    tasaVigente: {
      id: number;
      promedioBinance: number;
      comisionPct: number;
      tasaFinal: number;
      bancosActivos: string[];
      valoresBancos: {
        banesco: number;
        provincial: number;
        mercantil: number;
        venezuela: number;
      };
      responsible: string;
      createdAt: string;
      modo?: RateMode;
    } | null;
    datosEnVivo: {
      promedioBinanceActual: number;
      comisionConfigurada: number;
      tasaFinalCalculada: number;
      bancos: Record<string, { valor: number; activo: boolean }>;
    };
    hayCambios: boolean;
    simulacion: {
      ejemploUsdt: number;
      resultadoVes: number;
    };
  };
}

export interface VesUsdtPreviewResponse {
  success: boolean;
  data: {
    promedioBinance: number;
    comisionPct: number;
    tasaFinal: number;
    ejemplos: { usdt: number; ves: number }[];
    bancosActivos: string[];
  };
}

export interface VesUsdtSaveResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    promedioBinance: number;
    comisionPct: number;
    tasaFinal: number;
    bancosActivos: string[];
    valoresBancos: any;
    responsible: string;
    createdAt: string;
    tasaAnterior?: {
      id: number;
      tasaFinal: number;
    };
  };
}

export interface TasaVesClpPorMontoResponse {
  id: number;
  montoVES: number;
  montoCLP: number;
  clpEquivalente: number;
  rango: string;
  rangoNumerico: string;
  fuente: string;
  perfil: string;
  tasaClient: number;
  tasaAdmin: number;
  tasaAplicada: number;
  descripcion: string;
  fechaTasa: string;
}

export interface ActualizarParametrosRequest {
  operador: 'MANUAL' | 'BINANCE' | 'CBPAY';
  descuentoFiat?: number;
  comision?: number;
  comisionVesClp?: number;
  rateVesUsdt?: number;
  usdClp?: number;
  email?: string; // Opcional: el backend lo obtendrá de Cognito si no se proporciona
}

/** `{ version }` sólo si el GET del editor la trajo: sin ella el PUT va como siempre. */
function conVersion(version: string | null | undefined): { version?: string } {
  return typeof version === 'string' && version ? { version } : {};
}

@Injectable({
  providedIn: 'root',
})
export class MainRatesService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService
  ) {}

  /**
   * Método para obtener los headers con el token
   */
  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  /**
   * Headers de los editores de franjas y tramos (operadores): sin el
   * `ErrorInterceptor` global, que aplana todo lo que no sea 400/422/429 y el
   * `ApiRestInterceptor` lo convierte en un 401 sintético. Así `handleError`
   * recibe el status y el cuerpo reales y los editores muestran sus mensajes
   * propios ante 403/404/409/503. Ver `shared/utils/error-http-real.ts`.
   */
  private async getHeadersEditor(): Promise<HttpHeaders> {
    return sinInterceptorGlobal(await this.getHeaders());
  }

  /**
   * Manejo de errores HTTP
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      switch (error.status) {
        case 401:
          errorMessage =
            'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 400:
          errorMessage = error.error?.message || 'Solicitud incorrecta.';
          break;
        case 404:
          errorMessage = 'El servicio no está disponible en este momento.';
          break;
        case 500:
          errorMessage =
            'Error interno del servidor. Por favor, intente más tarde.';
          break;
        default:
          errorMessage = error.error?.message || errorMessage;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  /**
   * Calcula las tasas por rango sin guardar en BD
   */
  calcularTasas(): Observable<CalcularTasasResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<CalcularTasasResponse>(
          `${this.apiUrl}rates/calcular`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Calcula y guarda las tasas en la base de datos. Si alguna ruta no se
   * grabó, el back responde `success: false` con el motivo en `message`.
   */
  calcularYGuardarTasas(): Observable<CalcularTasasResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<CalcularTasasResponse>(
          `${this.apiUrl}rates/calcular-y-guardar`,
          {},
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtiene las últimas tasas guardadas
   * @param limit - Cantidad de registros a obtener (por defecto 1)
   */
  obtenerUltimasTasas(limit: number = 1): Observable<UltimasTasasResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<UltimasTasasResponse>(
          `${this.apiUrl}rates/ultimas?limit=${limit}`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtiene las últimas tasas agrupadas por rango
   */
  obtenerUltimasTasasAgrupadas(): Observable<TasasPorRangoResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<TasasPorRangoResponse>(
          `${this.apiUrl}rates/ultimas-agrupadas`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtiene la tasa para un monto específico
   * @param monto - Monto para calcular la tasa
   * @param perfil - Perfil de usuario (Client o Admin)
   */
  obtenerTasaPorMonto(
    monto: number,
    perfil: 'Client' | 'Admin' = 'Client'
  ): Observable<TasaPorMontoResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<TasaPorMontoResponse>(
          `${this.apiUrl}rates/por-monto?monto=${monto}&perfil=${perfil}`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtiene todos los valores actuales del sistema para el frontend
   * Incluye operador activo, parámetros y tasas actuales
   */
  obtenerValoresFrontend(): Observable<ValoresFrontendResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<ValoresFrontendResponse>(
          `${this.apiUrl}rates/valores-frontend`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Estado del markup de la tasa Aguacate contra el spot.
   *
   * El cálculo vive en el backend: ahí corre igual con el navegador cerrado,
   * y el umbral sale de la tabla de parámetros en vez de una constante del
   * bundle.
   */
  obtenerEstadoMarkup(): Observable<MarkupEstadoResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<MarkupEstadoResponse>(
          `${this.apiUrl}rates/markup/estado`,
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Registra que este operador dice haber avisado al encargado. Devuelve
   * hasta cuándo se le silencia el aviso.
   */
  registrarAcuseMarkup(ratio: number): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<any>(
          `${this.apiUrl}rates/markup/acuse`,
          { ratio },
          { headers },
        ),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Guarda el umbral de markup. El backend responde 403 si en producción el
   * correo no es el del encargado de la tasa.
   */
  guardarConfigMarkup(config: {
    maxRatio?: number;
    reaparicionSeg?: number;
  }): Observable<any> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.put<any>(`${this.apiUrl}rates/markup/config`, config, {
          headers,
        }),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  /**
   * Obtiene los valores de Binance (bancos) y otros operadores registrados
   * Para mostrar en el frontend de forma simple
   */
  obtenerValoresRegistrados(): Observable<ValoresRegistradosResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<ValoresRegistradosResponse>(
          `${this.apiUrl}rates/valores-registrados`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Cambia el operador activo y recalcula las tasas. La respuesta puede traer
   * `tasasGrabadas` (ver `TasasGrabadas`).
   * @param operador - Operador a activar (MANUAL, BINANCE o CBPAY)
   */
  cambiarOperador(operador: 'MANUAL' | 'BINANCE' | 'CBPAY'): Observable<any> {
    this.loadingSubject.next(true);
    const body: CambiarOperadorRequest = { operador };

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}rates/cambiar-operador`,
          body,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Actualiza parámetros específicos de un operador. La respuesta puede traer
   * `tasasGrabadas` (ver `TasasGrabadas`).
   * @param params - Parámetros a actualizar
   */
  actualizarParametros(params: ActualizarParametrosRequest): Observable<any> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}rates/actualizar-parametros`,
          params,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtiene las últimas tasas agrupadas por rango (PÚBLICO - sin autenticación)
   * Endpoint: GET /rates-public/ultimas-agrupadas
   */
  obtenerTasasPublicas(): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}rates-public/ultimas-agrupadas`
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene las tasas usando el endpoint por-monto (ALTERNATIVA TEMPORAL)
   * Llama al endpoint 3 veces con diferentes montos para obtener las 3 tasas
   */
  obtenerTasasPublicasAlternativa(): Observable<any> {
    // Montos representativos de cada rango
    const montoA = 50000;  // Rango A: 10,000 - 99,999
    const montoB = 150000; // Rango B: 100,000 - 209,999
    const montoC = 500000; // Rango C: 210,000 - 5,000,000

    const tasaA$ = this.http.get<any>(
      `${this.apiUrl}rates-public/por-monto?monto=${montoA}&perfil=Client`
    );
    const tasaB$ = this.http.get<any>(
      `${this.apiUrl}rates-public/por-monto?monto=${montoB}&perfil=Client`
    );
    const tasaC$ = this.http.get<any>(
      `${this.apiUrl}rates-public/por-monto?monto=${montoC}&perfil=Client`
    );

    // Combinar las 3 llamadas en paralelo
    return forkJoin({
      rangoA: tasaA$,
      rangoB: tasaB$,
      rangoC: tasaC$
    }).pipe(
      map(result => ({
        success: true,
        data: {
          rangoA: {
            client: { tasa: result.rangoA.tasaClient },
            admin: { tasa: result.rangoA.tasaAdmin }
          },
          rangoB: {
            client: { tasa: result.rangoB.tasaClient },
            admin: { tasa: result.rangoB.tasaAdmin }
          },
          rangoC: {
            client: { tasa: result.rangoC.tasaClient },
            admin: { tasa: result.rangoC.tasaAdmin }
          }
        }
      })),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene la configuración actual de bancos activos/inactivos
   * GET /rates/bancos-config
   */
  obtenerConfigBancos(): Observable<any> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<any>(
          `${this.apiUrl}rates/bancos-config`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Actualiza la configuración de bancos activos/inactivos. La respuesta puede
   * traer `tasasGrabadas` (ver `TasasGrabadas`).
   * PUT /rates/bancos-config
   * @param config - Configuración de bancos { MERCANTIL?: boolean, BANESCO?: boolean, VENEZUELA?: boolean, PROVINCIAL?: boolean }
   */
  actualizarConfigBancos(config: {
    MERCANTIL?: boolean;
    BANESCO?: boolean;
    VENEZUELA?: boolean;
    PROVINCIAL?: boolean;
  }): Observable<any> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.put<any>(
          `${this.apiUrl}rates/bancos-config`,
          config,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  // ============================================================
  // MÉTODOS PARA TASA USDT → VES
  // ============================================================

  /**
   * Obtiene la configuración actual de tasa USDT→VES
   * GET /rates/usdt-ves/config
   */
  obtenerUsdtVesConfig(): Observable<UsdtVesConfigResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<UsdtVesConfigResponse>(
          `${this.apiUrl}rates/usdt-ves/config`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Preview de tasa USDT→VES sin guardar
   * GET /rates/usdt-ves/preview?comisionPct=X
   */
  previewUsdtVesRate(comisionPct: number): Observable<UsdtVesPreviewResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<UsdtVesPreviewResponse>(
          `${this.apiUrl}rates/usdt-ves/preview?comisionPct=${comisionPct}`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene la tasa USDT→VES vigente para usuarios
   */
  getUsdtVesRateForUser(): Observable<{
    success: boolean;
    data?: {
      tasaFinal: number;
      promedioBinance: number;
      comisionPct: number;
      updatedAt: string;
    };
    message?: string;
  }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<any>(
          `${this.apiUrl}rates/usdt-ves/rate`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene la tasa VES→USDT vigente para usuarios (compra de USDT con VES)
   */
  getVesUsdtRateForUser(): Observable<{
    success: boolean;
    data?: {
      tasaFinal: number;
      promedioBinance: number;
      comisionPct: number;
      updatedAt: string;
    };
    message?: string;
  }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<any>(
          `${this.apiUrl}rates/ves-usdt/rate`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Guarda nueva configuración de tasa USDT→VES
   * POST /rates/usdt-ves/config
   */
  guardarUsdtVesConfig(
    payload: SaveRateConfigPayload
  ): Observable<UsdtVesSaveResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<UsdtVesSaveResponse>(
          `${this.apiUrl}rates/usdt-ves/config`,
          payload,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtiene historial de tasas USDT→VES
   * GET /rates/usdt-ves/history?page=1&limit=20
   */
  obtenerUsdtVesHistory(page: number = 1, limit: number = 20): Observable<UsdtVesHistoryResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<UsdtVesHistoryResponse>(
          `${this.apiUrl}rates/usdt-ves/history?page=${page}&limit=${limit}`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  // ============================================================
  // MÉTODOS PARA TASA VES → USDT
  // ============================================================

  /**
   * Obtiene la configuración actual de tasa VES→USDT
   * GET /rates/ves-usdt/config
   */
  obtenerVesUsdtConfig(): Observable<VesUsdtConfigResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<VesUsdtConfigResponse>(
          `${this.apiUrl}rates/ves-usdt/config`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Preview de tasa VES→USDT sin guardar
   * GET /rates/ves-usdt/preview?comisionPct=X
   */
  previewVesUsdtRate(comisionPct: number): Observable<VesUsdtPreviewResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<VesUsdtPreviewResponse>(
          `${this.apiUrl}rates/ves-usdt/preview?comisionPct=${comisionPct}`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene la tasa VES→CLP para un monto VES específico (PÚBLICO)
   * GET /rates-public/ves-clp/por-monto?monto=X&perfil=Client
   */
  obtenerTasaVesClpPorMonto(
    monto: number,
    perfil: 'Client' | 'Admin' = 'Client'
  ): Observable<TasaVesClpPorMontoResponse> {
    return this.http.get<TasaVesClpPorMontoResponse>(
      `${this.apiUrl}rates-public/ves-clp/por-monto?monto=${monto}&perfil=${perfil}`
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene las tasas VES→CLP agrupadas por rango (PÚBLICO)
   * GET /rates-public/ves-clp/ultimas-agrupadas
   */
  obtenerTasasVesClpPublicas(): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}rates-public/ves-clp/ultimas-agrupadas`
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Obtiene las últimas tasas VES→CLP agrupadas por rango
   * GET /rates/ves-clp/ultimas-agrupadas
   */
  obtenerTasasVesCLPAgrupadas(): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<any>(
          `${this.apiUrl}rates/ves-clp/ultimas-agrupadas`,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Guarda nueva configuración de tasa VES→USDT
   * POST /rates/ves-usdt/config
   */
  guardarVesUsdtConfig(
    payload: SaveRateConfigPayload
  ): Observable<VesUsdtSaveResponse> {
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<VesUsdtSaveResponse>(
          `${this.apiUrl}rates/ves-usdt/config`,
          payload,
          { headers }
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  // ───────────────────────── Franjas dinámicas (operadores) ─────────────────────────

  /**
   * Franjas vigentes por par y de dónde salen (tabla o respaldo constante).
   * GET /rates/franjas/estado
   */
  obtenerEstadoFranjas(): Observable<EstadoFranjasResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<EstadoFranjasResponse>(
          `${this.apiUrl}rates/franjas/estado`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Franjas de la tabla del par, su tasa de este momento y el historial.
   * GET /rates/franjas/:par
   */
  obtenerEditorFranjas(par: ParFranja): Observable<EditorFranjasResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<EditorFranjasResponse>(
          `${this.apiUrl}rates/franjas/${par}`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Valida y calcula una propuesta sin guardar nada.
   * POST /rates/franjas/:par/previsualizar
   */
  previsualizarFranjas(
    par: ParFranja,
    franjas: EntradaFranja[]
  ): Observable<PrevisualizarFranjasResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.post<PrevisualizarFranjasResponse>(
          `${this.apiUrl}rates/franjas/${par}/previsualizar`,
          { franjas },
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Historial de cambios del par, paginado (página 1 = lo más reciente).
   * GET /rates/franjas/:par/historial?pagina=N&tamano=5
   */
  obtenerHistorialFranjas(
    par: ParFranja,
    pagina: number,
    tamano = 5
  ): Observable<HistorialFranjasPagina> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<HistorialFranjasPagina>(
          `${this.apiUrl}rates/franjas/${par}/historial`,
          { headers, params: { pagina: String(pagina), tamano: String(tamano) } }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Guarda el conjunto COMPLETO de franjas del par con su motivo. Si no valida
   * el backend responde 400 con `errores`; llegan en `originalError.error`.
   * Con `version` (la del GET del editor), 409 si otro operador guardó antes.
   * PUT /rates/franjas/:par
   */
  guardarFranjas(
    par: ParFranja,
    franjas: EntradaFranja[],
    motivo: string,
    version?: string | null
  ): Observable<GuardarFranjasResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.put<GuardarFranjasResponse>(
          `${this.apiUrl}rates/franjas/${par}`,
          { franjas, motivo, ...conVersion(version) },
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * ¿El operador logueado puede editar franjas y tramos? Lo decide el back.
   * GET /rates/franjas/permisos → `{ puedeEditar: boolean }`.
   *
   * Nunca lanza: `null` si falla, no existe todavía o responde otra cosa, y
   * el llamador cae a la lógica de siempre del front.
   */
  obtenerPermisosFranjas(): Observable<boolean | null> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<{ puedeEditar?: unknown }>(
          `${this.apiUrl}rates/franjas/permisos`,
          { headers }
        )
      ),
      map((r) => (typeof r?.puedeEditar === 'boolean' ? r.puedeEditar : null)),
      catchError(() => of(null))
    );
  }

  // ───────────────────────── Motor de márgenes CLP↔VES (03/11/2026) ─────────────────────────

  /**
   * Motor activo, márgenes guardados, spot cruzado y el reparto por franja.
   * Con los headers de los editores: un 404/503 (back sin el motor) llega
   * real y el llamador decide (las píldoras no se pintan, el modal avisa).
   * GET /rates/margenes-ves
   */
  obtenerMargenesVes(): Observable<MargenesVesResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<MargenesVesResponse>(
          `${this.apiUrl}rates/margenes-ves`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Guarda los 4 márgenes (y el motor, si viaja). Si no valida, 400 con
   * `message` (texto o lista) en `originalError.error`; en prod, 403 si el
   * operador no puede editar tasas; con `version` vieja (otro operador guardó
   * antes), 409 `{ message }` y no se guarda nada.
   * PUT /rates/margenes-ves
   */
  guardarMargenesVes(body: GuardarMargenesVesBody): Observable<GuardarMargenesVesResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.put<GuardarMargenesVesResponse>(
          `${this.apiUrl}rates/margenes-ves`,
          body,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  // ───────────────────────── Tramos VES→USDT ─────────────────────────

  /**
   * Tasa de cada tramo VES→USDT con la base vigente (lo ve cualquier usuario).
   * GET /rates/tramos-usdt/ves-usdt
   */
  obtenerTramosVesUsdt(): Observable<EstadoTramosUsdtResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<EstadoTramosUsdtResponse>(
          `${this.apiUrl}rates/tramos-usdt/ves-usdt`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Comisión, margen mínimo, todos los tramos (también los apagados), sus
   * tasas y la primera página del historial.
   * GET /rates/tramos-usdt/ves-usdt/editor
   */
  obtenerEditorTramosVesUsdt(): Observable<EditorTramosUsdtResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<EditorTramosUsdtResponse>(
          `${this.apiUrl}rates/tramos-usdt/ves-usdt/editor`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Valida y calcula una propuesta sin guardar nada.
   * POST /rates/tramos-usdt/ves-usdt/previsualizar
   */
  previsualizarTramosVesUsdt(
    cuerpo: CuerpoTramosUsdt
  ): Observable<PrevisualizarTramosUsdtResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.post<PrevisualizarTramosUsdtResponse>(
          `${this.apiUrl}rates/tramos-usdt/ves-usdt/previsualizar`,
          cuerpo,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Guarda margen mínimo y el conjunto COMPLETO de tramos con su motivo (la
   * comisión no viaja). Si no valida responde 400 con `errores` (en
   * `originalError.error`); en prod, 403 si el operador no está autorizado.
   * PUT /rates/tramos-usdt/ves-usdt
   */
  guardarTramosVesUsdt(
    cuerpo: CuerpoTramosUsdt,
    motivo: string,
    version?: string | null
  ): Observable<GuardarTramosUsdtResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.put<GuardarTramosUsdtResponse>(
          `${this.apiUrl}rates/tramos-usdt/ves-usdt`,
          { ...cuerpo, motivo, ...conVersion(version) },
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Historial de tramos, comisión y margen mínimo, paginado (1 = lo último).
   * GET /rates/tramos-usdt/ves-usdt/historial?pagina=N&tamano=5
   */
  obtenerHistorialTramosVesUsdt(
    pagina: number,
    tamano = 5
  ): Observable<HistorialTramosUsdtPagina> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<HistorialTramosUsdtPagina>(
          `${this.apiUrl}rates/tramos-usdt/ves-usdt/historial`,
          { headers, params: { pagina: String(pagina), tamano: String(tamano) } }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  // ───────────────────────── Tramos USDT→VES (espejo) ─────────────────────────

  /** GET /rates/tramos-usdt/usdt-ves (cualquier usuario logueado). */
  obtenerTramosUsdtVes(): Observable<EstadoTramosUsdtResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<EstadoTramosUsdtResponse>(
          `${this.apiUrl}rates/tramos-usdt/usdt-ves`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /** GET /rates/tramos-usdt/usdt-ves/editor */
  obtenerEditorTramosUsdtVes(): Observable<EditorTramosUsdtVesResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<EditorTramosUsdtVesResponse>(
          `${this.apiUrl}rates/tramos-usdt/usdt-ves/editor`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /** POST /rates/tramos-usdt/usdt-ves/previsualizar */
  previsualizarTramosUsdtVes(
    cuerpo: CuerpoTramosUsdt
  ): Observable<PrevisualizarTramosUsdtVesResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.post<PrevisualizarTramosUsdtVesResponse>(
          `${this.apiUrl}rates/tramos-usdt/usdt-ves/previsualizar`,
          cuerpo,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /** PUT /rates/tramos-usdt/usdt-ves (en prod, 403 si el operador no está autorizado). */
  guardarTramosUsdtVes(
    cuerpo: CuerpoTramosUsdt,
    motivo: string,
    version?: string | null
  ): Observable<GuardarTramosUsdtVesResponse> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.put<GuardarTramosUsdtVesResponse>(
          `${this.apiUrl}rates/tramos-usdt/usdt-ves`,
          { ...cuerpo, motivo, ...conVersion(version) },
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /** GET /rates/tramos-usdt/usdt-ves/historial?pagina=N&tamano=5 */
  obtenerHistorialTramosUsdtVes(
    pagina: number,
    tamano = 5
  ): Observable<HistorialTramosUsdtPagina> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<HistorialTramosUsdtPagina>(
          `${this.apiUrl}rates/tramos-usdt/usdt-ves/historial`,
          { headers, params: { pagina: String(pagina), tamano: String(tamano) } }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  // ───────────────────────── Tramos CLP→USDT y USDT→CLP (22/10) ─────────────────────────
  //
  // `par` = 'clp-usdt' (compra) o 'usdt-clp' (venta). Mismas rutas y forma que
  // las de VES; el cuerpo suma `topePct`. Backend viejo o sin SQL: 404/503, y
  // el llamador cae a la tasa de siempre.

  /** GET /rates/tramos-usdt/clp-usdt (cualquier usuario logueado): lo que se cobra. */
  obtenerTramosClpUsdt(): Observable<EstadoTramosClpUsdtResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<EstadoTramosClpUsdtResponse>(
          `${this.apiUrl}rates/tramos-usdt/clp-usdt`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /** GET /rates/tramos-usdt/usdt-clp (cualquier usuario logueado). */
  obtenerTramosUsdtClp(): Observable<EstadoTramosUsdtClpResponse> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<EstadoTramosUsdtClpResponse>(
          `${this.apiUrl}rates/tramos-usdt/usdt-clp`,
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /** GET /rates/tramos-usdt/clp-usdt/editor */
  obtenerEditorTramosClpUsdt(): Observable<EditorTramosClpUsdtResponse> {
    return this.editorTramosClp<EditorTramosClpUsdtResponse>('clp-usdt');
  }

  /** GET /rates/tramos-usdt/usdt-clp/editor */
  obtenerEditorTramosUsdtClp(): Observable<EditorTramosUsdtClpResponse> {
    return this.editorTramosClp<EditorTramosUsdtClpResponse>('usdt-clp');
  }

  /** POST /rates/tramos-usdt/clp-usdt/previsualizar */
  previsualizarTramosClpUsdt(cuerpo: CuerpoTramosClp): Observable<PrevisualizarTramosClpUsdtResponse> {
    return this.previsualizarTramosClp<PrevisualizarTramosClpUsdtResponse>('clp-usdt', cuerpo);
  }

  /** POST /rates/tramos-usdt/usdt-clp/previsualizar */
  previsualizarTramosUsdtClp(cuerpo: CuerpoTramosClp): Observable<PrevisualizarTramosUsdtClpResponse> {
    return this.previsualizarTramosClp<PrevisualizarTramosUsdtClpResponse>('usdt-clp', cuerpo);
  }

  /** PUT /rates/tramos-usdt/clp-usdt (en prod, 403 si el operador no está autorizado; 409 si otro guardó). */
  guardarTramosClpUsdt(
    cuerpo: CuerpoTramosClp,
    motivo: string,
    version?: string | null
  ): Observable<EditorTramosClpUsdtResponse & { ok: true; cambios: number }> {
    return this.guardarTramosClp('clp-usdt', cuerpo, motivo, version);
  }

  /** PUT /rates/tramos-usdt/usdt-clp */
  guardarTramosUsdtClp(
    cuerpo: CuerpoTramosClp,
    motivo: string,
    version?: string | null
  ): Observable<EditorTramosUsdtClpResponse & { ok: true; cambios: number }> {
    return this.guardarTramosClp('usdt-clp', cuerpo, motivo, version);
  }

  /** GET /rates/tramos-usdt/clp-usdt/historial?pagina=N&tamano=5 */
  obtenerHistorialTramosClpUsdt(pagina: number, tamano = 5): Observable<HistorialTramosUsdtPagina> {
    return this.historialTramosClp('clp-usdt', pagina, tamano);
  }

  /** GET /rates/tramos-usdt/usdt-clp/historial?pagina=N&tamano=5 */
  obtenerHistorialTramosUsdtClp(pagina: number, tamano = 5): Observable<HistorialTramosUsdtPagina> {
    return this.historialTramosClp('usdt-clp', pagina, tamano);
  }

  private editorTramosClp<T>(par: 'clp-usdt' | 'usdt-clp'): Observable<T> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<T>(`${this.apiUrl}rates/tramos-usdt/${par}/editor`, { headers })
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  private previsualizarTramosClp<T>(par: 'clp-usdt' | 'usdt-clp', cuerpo: CuerpoTramosClp): Observable<T> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.post<T>(`${this.apiUrl}rates/tramos-usdt/${par}/previsualizar`, cuerpo, { headers })
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  private guardarTramosClp<T>(
    par: 'clp-usdt' | 'usdt-clp',
    cuerpo: CuerpoTramosClp,
    motivo: string,
    version?: string | null
  ): Observable<T> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.put<T>(
          `${this.apiUrl}rates/tramos-usdt/${par}`,
          { ...cuerpo, motivo, ...conVersion(version) },
          { headers }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  private historialTramosClp(
    par: 'clp-usdt' | 'usdt-clp',
    pagina: number,
    tamano: number
  ): Observable<HistorialTramosUsdtPagina> {
    return from(this.getHeadersEditor()).pipe(
      switchMap((headers) =>
        this.http.get<HistorialTramosUsdtPagina>(
          `${this.apiUrl}rates/tramos-usdt/${par}/historial`,
          { headers, params: { pagina: String(pagina), tamano: String(tamano) } }
        )
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }
}
